import { useEffect, useRef, useState } from "react";
import styles from "./SproutAnimation.module.css";

/**
 * Hand-drawn sprout that plays a one-shot growth sequence the first time it
 * scrolls into view: soil draws in, stem grows, two cotyledons unfurl, a
 * second smaller pair of leaves opens, and finally a tiny "Y" of new growth
 * appears at the apex. Decorative — `aria-hidden`. Honors `prefers-reduced-motion`.
 */
export default function SproutAnimation() {
  const ref = useRef<HTMLDivElement>(null);
  const [played, setPlayed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || played) return;

    if (typeof IntersectionObserver === "undefined") {
      setPlayed(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setPlayed(true);
            observer.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35, rootMargin: "0px 0px -8% 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [played]);

  return (
    <div
      ref={ref}
      className={`${styles.wrap} ${played ? styles.play : ""}`}
      aria-hidden="true"
    >
      <svg
        className={styles.svg}
        viewBox="0 0 220 280"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        >
          {/* soil line */}
          <path
            className={`${styles.draw} ${styles.ground}`}
            pathLength={100}
            d="M 28 230 C 60 226 100 232 130 228 C 160 224 184 230 196 226"
            strokeWidth={1.6}
          />

          {/* tiny soil specks */}
          <g className={styles.fade}>
            <path d="M 50 246 L 60 242" strokeWidth={1.2} opacity="0.55" />
            <path d="M 88 250 L 98 246" strokeWidth={1.2} opacity="0.55" />
            <path d="M 132 246 L 142 242" strokeWidth={1.2} opacity="0.55" />
            <path d="M 168 248 L 178 244" strokeWidth={1.2} opacity="0.55" />
          </g>

          {/* main stem — soil-line up to apex with one gentle organic curve */}
          <path
            className={`${styles.draw} ${styles.stem}`}
            pathLength={100}
            d="M 110 230 C 108 200 112 168 110 138 C 108 108 112 78 110 56"
            strokeWidth={1.8}
          />

          {/* lower-right cotyledon — large almond leaf, with center vein */}
          <g className={`${styles.unfurl} ${styles.leafLowerRight}`}>
            <path
              d="M 110 180 Q 142 158 174 142 Q 148 174 110 180 Z"
              strokeWidth={1.5}
            />
            <path
              d="M 116 178 Q 140 168 168 146"
              strokeWidth={1}
              opacity="0.55"
            />
          </g>

          {/* lower-left cotyledon — mirrored */}
          <g className={`${styles.unfurl} ${styles.leafLowerLeft}`}>
            <path
              d="M 110 180 Q 78 158 46 142 Q 72 174 110 180 Z"
              strokeWidth={1.5}
            />
            <path
              d="M 104 178 Q 80 168 52 146"
              strokeWidth={1}
              opacity="0.55"
            />
          </g>

          {/* upper-right leaf — smaller */}
          <g className={`${styles.unfurl} ${styles.leafUpperRight}`}>
            <path
              d="M 110 122 Q 134 108 158 100 Q 138 124 110 122 Z"
              strokeWidth={1.4}
            />
            <path
              d="M 114 121 Q 132 114 154 103"
              strokeWidth={0.95}
              opacity="0.5"
            />
          </g>

          {/* upper-left leaf — mirrored */}
          <g className={`${styles.unfurl} ${styles.leafUpperLeft}`}>
            <path
              d="M 110 122 Q 86 108 62 100 Q 82 124 110 122 Z"
              strokeWidth={1.4}
            />
            <path
              d="M 106 121 Q 88 114 66 103"
              strokeWidth={0.95}
              opacity="0.5"
            />
          </g>

          {/* apex — tiny "Y" of new growth at the top of the stem */}
          <g className={`${styles.unfurl} ${styles.tip}`}>
            <path d="M 110 60 C 107 53 102 50 97 47" strokeWidth={1.4} />
            <path d="M 110 60 C 113 53 118 50 123 47" strokeWidth={1.4} />
          </g>
        </g>
      </svg>
    </div>
  );
}
